import { ImageProps } from '@src/quince-components/src'

import ContentfulImage, { ContentfulImageForMenuItem } from './contentful/ContentfulImage'
import Link from './Link'

export type IMAGE_LOADING_TYPE = 'eager' | 'lazy'

interface IImage {
  readonly id: string
  readonly title?: string
  readonly description?: string
  readonly url: string
  readonly fileName: string
  readonly width: number
  readonly height: number
  readonly loading?: IMAGE_LOADING_TYPE
  quality?: number
  readonly link?: Link
  alt?: string
  readonly className?: string
}

export default class Image implements IImage {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly url: string
  readonly fileName: string
  readonly height: number
  readonly width: number
  readonly className?: string

  quality: number
  loading?: IMAGE_LOADING_TYPE
  readonly link?: Link
  alt?: string

  constructor({
    title = '',
    description = '',
    url,
    fileName,
    id,
    height,
    width,
    loading,
    quality = 50,
    link,
    alt,
    className,
  }: IImage) {
    this.id = id
    this.title = title
    this.description = description
    this.url = url
    this.fileName = fileName
    this.height = height
    this.width = width
    this.loading = loading
    this.quality = quality > 100 ? 100 : quality
    this.link = link
    this.alt = alt
    this.className = className

    return {
      ...this,
    }
  }

  static makeImageProps(image?: Image, link?: Link): ImageProps {
    if (!image) {
      return {
        url: '',
        title: '',
        alt: '',
        height: 100,
        width: 100,
      }
    }
    return {
      url: image.url,
      title: image.title,
      id: image.id,
      description: image.description,
      fileName: image.fileName,
      height: image.height,
      width: image.width,
      loading: image.loading,
      quality: image.quality,
      link: link,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      alt: image.alt || image.title || 'image',
    }
  }

  static fromContentful(contentfulImage: ContentfulImage | ContentfulImageForMenuItem | undefined) {
    if (!contentfulImage) {
      return new Image({
        id: '',
        url: '',
        fileName: '',
        height: 0,
        width: 0,
        title: '',
        description: '',
        loading: 'lazy',
      })
    }

    // Handle both ContentfulImage and ContentfulImageForMenuItem
    const isContentfulImage = 'file' in contentfulImage
    const url = isContentfulImage ? contentfulImage.file.url : contentfulImage.url || ''
    const fileName = isContentfulImage ? contentfulImage.file.fileName : contentfulImage.fileName || ''
    const height = isContentfulImage ? contentfulImage.file.details.image.height : contentfulImage.height || 0
    const width = isContentfulImage ? contentfulImage.file.details.image.width : contentfulImage.width || 0

    return new Image({
      title: contentfulImage.title ?? '',
      description: contentfulImage.description ?? '',
      url,
      fileName,
      id: contentfulImage.id || '',
      height,
      width,
      loading: contentfulImage.loading ?? 'lazy',
    })
  }
}
